import { Component, ChangeDetectionStrategy, OnInit, ChangeDetectorRef } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import { Store, Select, Actions, ofActionSuccessful } from '@ngxs/store';
import { OpenMenuAction, CloseMenuAction, SetThemeAction, UpdateSignalRConnectionStatusAction, RegisterSignalRHandlersForUiAction } from '@ngxs-store/ui-store/ui.actions';
import { UiState } from '@ngxs-store/ui-store/ui.state';
import { Router, ActivatedRoute, Route, Routes, NavigationEnd, RouterEvent } from '@angular/router';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { LayoutLoginDialogComponent } from '@core/components/layout-login-dialog/layout-login-dialog.component';
import { AuthState } from '@ngxs-store/auth-store/auth.state';
import { DecodedToken, LastLoggedInUser } from '@core/models';
import { ClearTokenAction, LoginAction, ClearLastUserAction, ChangeTenantAction } from '@ngxs-store/auth-store/auth.actions';
import { LayoutLoginBottomSheetComponent } from '@core/components/layout-login-bottom-sheet/layout-login-bottom-sheet.component';
import { Navigate } from '@ngxs/router-plugin';
import { AuthenticateUserCommand } from '@core/services/api.service';
import { SignalRService } from '@core/services/signalr.service';
import { LayoutSignalRDialogComponent } from '@core/components/layout-signalr-dialog/layout-signalr-dialog.component';
import { TenantModel } from '@core/models/auth/tenant.model';

@Component({
  selector: 'app-layout-page',
  template: `
    <div class="layout-container">
      <app-layout-toolbar
        [title]="title"
        [showMenu]="showMenu$ | async"
        [signalRConnected]="signalRConnected$ | async"
        [tenants]="tenants"
        [theme]="theme$ | async"
        [currentTenant]="tenant$ | async"
        (openSignalRDialogClicked)="openSignalRDialog()"
        (signalRConnectionStateChanged)="signalRConnectionStateChanged($event)"
        (disconnectSignalRClicked)="disconnectSignalR()"
        (openMenuClick)="openMenu()"
        (closeMenuClick)="closeMenu()"
        (openProfileClick)="openProfile()"
        (tenantSelectionChanged)="switchTenant($event)"
      >
      </app-layout-toolbar>
      <app-layout-sidenav-container
        [currentRoute]="currentRoute"
        [sidenavRoutes]="sidenavRoutes"
        [showMenu]="showMenu$ | async"
        [isMobileMode]="isMobileMode$ | async"
        (closeMenuClick)="closeMenu()"
      >
      </app-layout-sidenav-container>
    </div>
  `,
  styles: [
    `
      .layout-container {
        display: flex;
        flex-direction: column;
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LayoutPageComponent implements OnInit {
  @Select(UiState.getShowMenu) showMenu$: Observable<boolean>;
  @Select(UiState.getIsMobileMode) isMobileMode$: Observable<boolean>;
  @Select(UiState.getSignalRConnectionState) signalRConnected$: Observable<boolean>;
  @Select(UiState.getTheme) theme$: Observable<string>;
  @Select(AuthState.getDecodedToken) decodedToken$: Observable<DecodedToken>;
  @Select(AuthState.getHasValidToken) hasValidToken$: Observable<boolean>;
  @Select(AuthState.getLastLoggedInUser) lastLoggedInUser$: Observable<LastLoggedInUser>;
  @Select(AuthState.getTenant) tenant$: Observable<TenantModel>;
  @Select(AuthState.getIsLoading) isLoading$: Observable<boolean>;
  @Select(AuthState.getHasAuthenticatorConfigured) hasAuthenticatorConfigured$: Observable<boolean>;

  title: string;
  currentRoute: string;
  tenants: TenantModel[] = [];
  sidenavRoutes: Routes = [];

  constructor(
    private store: Store,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public signalRService: SignalRService,
    private actions$: Actions,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog,
    private bottomSheet: MatBottomSheet
  ) {
    this.signalRService.startConnection();
    router.events.subscribe((event: RouterEvent) => this.routeChangedEvent(event));
    this.store.dispatch(new RegisterSignalRHandlersForUiAction());
  }

  ngOnInit() {
    this.isMobileMode$.subscribe((isMobileMode) => {
      isMobileMode ? this.closeMenu() : this.openMenu();
      isMobileMode ? (this.title = environment.mobileTitle) : (this.title = environment.title);
    });

    this.decodedToken$.subscribe((token) => {
      this.tenants = [];
      token.tenants.forEach(item => {
        const id: string = item.split('|')[0];
        const name: string = item.split('|')[1];
        this.tenants.push({ id, name });
      });

      this.sidenavRoutes = [];
      this.initSidenavRoutes(this.router.config, token.roles);
      this.sidenavRoutes.sort((a, b) => a.data.seq - b.data.seq);

      // tslint:disable-next-line: no-string-literal
      if (!this.cdr['destroyed']) {
        this.cdr.detectChanges();
      }
    });

    this.actions$.pipe(ofActionSuccessful(ClearTokenAction)).subscribe(() => {
      this.signalRService.stopConnection();
    });
  }

  switchTenant(tenant: TenantModel) {
    this.store.dispatch(new ChangeTenantAction({ tenant }));
  }

  initSidenavRoutes(routes: Route[], userRoles: string[]) {
    routes.forEach((route) => {
      if (route.data && route.data.url && route.data.show) {
        if (route.data.roles === undefined || route.data.roles.length === 0) {
          this.sidenavRoutes.push(route);
        } else {
          if (this.hasRequiredRoles(route.data.roles, userRoles)) {
            this.sidenavRoutes.push(route);
          }
        }
      }

      if (route.children) {
        this.initSidenavRoutes(route.children, userRoles);
      }
    });
  }

  hasRequiredRoles(routeRoles: string[], userRoles: string[]) {
    return routeRoles.some((routeRole) => {
      return (userRoles.indexOf(routeRole) >= 0);
    });
  }

  routeChangedEvent(event: RouterEvent) {
    if (event instanceof NavigationEnd) {
      const routeData: any = this.activatedRoute.snapshot.children[0].data;
      this.currentRoute = routeData.url;
    }
  }

  openMenu() {
    this.store.dispatch(new OpenMenuAction());
  }

  closeMenu() {
    this.store.dispatch(new CloseMenuAction());
  }

  openProfile() {
    const isMobileMode = this.store.selectSnapshot(UiState.getIsMobileMode);

    const config = {
      autoFocus: false,
      data: {
        decodedToken$: this.decodedToken$,
        hasValidToken$: this.hasValidToken$,
        lastLoggedInUser$: this.lastLoggedInUser$,
        isLoading$: this.isLoading$,
        theme$: this.theme$,
        isMobileMode$: this.isMobileMode$
      }
    };

    const componentRef = isMobileMode ? this.bottomSheet.open(LayoutLoginBottomSheetComponent, config) : this.dialog.open(LayoutLoginDialogComponent, config);
    const componentProp = isMobileMode ? 'instance' : 'componentInstance';

    componentRef[componentProp].loginClicked.subscribe(($authenticateUserCommand: AuthenticateUserCommand) => {
      this.store.dispatch(new LoginAction({ authenticateUserCommand: $authenticateUserCommand }));
    });

    componentRef[componentProp].logoutClicked.subscribe(() => {
      this.store.dispatch(new ClearTokenAction());
    });

    componentRef[componentProp].clearLastUserClicked.subscribe(() => {
      this.store.dispatch(new ClearLastUserAction());
    });

    componentRef[componentProp].forgotPasswordClicked.subscribe(() => {
      this.store.dispatch(new Navigate(['/auth/request-forgoten-password']));
    });

    componentRef[componentProp].selectThemeClicked.subscribe((theme: string) => {
      this.store.dispatch(new SetThemeAction({ theme }));
    });
  }

  signalRConnectionStateChanged(signalRConnected: boolean) {
    this.dialog.closeAll();

    if (!signalRConnected && environment.blockUiOnSignalrDisconnect) {
      this.openSignalRDialog();
    }
  }

  openSignalRDialog() {
    this.dialog.open(LayoutSignalRDialogComponent, { disableClose: environment.blockUiOnSignalrDisconnect, width: '310px', panelClass: 'signalr-dialog' })
      .componentInstance.reconnectClicked.subscribe(() => {
        this.signalRService.startConnection();
      });
  }

  disconnectSignalR() {
    this.signalRService.getHubConnection()
      .stop()
      .then(() => this.store.dispatch(new UpdateSignalRConnectionStatusAction({ connected: false })));
  }
}
